import { darken } from '@mui/material/styles';
import { useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { ReportWidgetChartTooltip } from './ReportWidgetChartTooltip';

import type {
    ReportWidgetChartPoint,
    ReportWidgetItem,
    ReportWidgetTooltipRowsBuilder,
} from '../../model/types';

interface ReportWidgetBarChartProps {
    data: ReportWidgetChartPoint[];
    showLegend: boolean;
    items: ReportWidgetItem[];
    total: number;
    tooltipFormatter: (value: number) => string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
    onItemClick?: (item: ReportWidgetItem) => void;
    width?: number;
    height?: number;
    activeItemId?: string;
    disableAnimation?: boolean;
    onItemHover?: (item: ReportWidgetItem | null) => void;
    yAxisTickFormatter?: (value: number) => string;
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: ReportWidgetChartPoint[],
    ) => string;
}

export const ReportWidgetBarChart = ({
    data,
    showLegend,
    tooltipFormatter,
    tooltipRowsBuilder,
    items,
    total,
    onItemClick,
    width,
    height,
    activeItemId,
    disableAnimation = false,
    onItemHover,
    yAxisTickFormatter,
    xAxisTickFormatter,
}: ReportWidgetBarChartProps) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    return (
        <BarChart data={data} width={width} height={height}>
            <CartesianGrid stroke="#E0E0E0" strokeWidth={1} vertical={false} />
            <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: '#000000' }}
                tickLine={false}
                interval={0}
                tickFormatter={(value: string | number, index: number) =>
                    xAxisTickFormatter
                        ? xAxisTickFormatter(value, index, data)
                        : String(value)
                }
                axisLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
            />
            <YAxis
                tick={{ fontSize: 12, fill: '#000000' }}
                tickLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
                tickSize={10}
                tickMargin={6}
                tickFormatter={(v) => {
                    if (yAxisTickFormatter) {
                        return yAxisTickFormatter(v as number);
                    }

                    if (typeof v !== 'number') {
                        return String(v);
                    }

                    if (v === 0) {
                        return '0';
                    }

                    return tooltipFormatter(v);
                }}
                domain={[
                    'auto',
                    (dataMax: number) =>
                        Number.isFinite(dataMax) ? dataMax * 1.2 : dataMax,
                ]}
                axisLine={{ stroke: '#E0E0E0', strokeWidth: 1 }}
            />
            <Tooltip
                cursor={{ fill: 'transparent' }}
                content={
                    <ReportWidgetChartTooltip
                        tooltipFormatter={tooltipFormatter}
                        tooltipRowsBuilder={tooltipRowsBuilder}
                        total={total}
                        items={items}
                    />
                }
            />
            {showLegend && (
                <Legend
                    verticalAlign="top"
                    align="left"
                    iconType="circle"
                    wrapperStyle={{
                        paddingBottom: '18px',
                        fontSize: '12px',
                        lineHeight: '16px',
                    }}
                />
            )}
            <Bar
                dataKey="value"
                fill="#5ca1ff"
                radius={[4, 4, 0, 0]}
                isAnimationActive={!disableAnimation}
                cursor={onItemClick ? 'pointer' : 'default'}
                maxBarSize={150}
            >
                {data.map((entry, index) => {
                    const item = items[index];
                    const isHovered = entry.id === hoveredId;
                    const isActive = entry.id === activeItemId;

                    const baseColor = entry.color;
                    const fillColor =
                        isHovered || isActive
                            ? darken(baseColor, 0.2)
                            : baseColor;

                    return (
                        <Cell
                            key={entry.id}
                            fill={fillColor}
                            style={{
                                transition: 'fill 120ms ease-out',
                            }}
                            onClick={() =>
                                onItemClick && item && onItemClick(item)
                            }
                            onMouseEnter={() => {
                                setHoveredId(entry.id);
                                if (onItemHover && item) {
                                    onItemHover(item);
                                }
                            }}
                            onMouseLeave={() => {
                                setHoveredId((current) =>
                                    current === entry.id ? null : current,
                                );
                                if (onItemHover) {
                                    onItemHover(null);
                                }
                            }}
                        />
                    );
                })}
            </Bar>
        </BarChart>
    );
};
